import type { RefCallback } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { trackClientEvent } from 'analytics/analytics'
import { useInView } from 'react-intersection-observer'

type TileType = 'category' | 'brand'

interface TileItem {
  id: string
  name: string
}

function useMerchandiseTileTracking(tileType: TileType, item: TileItem | undefined, position = 0) {
  const listName = `merchandise_${tileType}_carousel`
  const [hasTrackedImpression, setHasTrackedImpression] = useState(false)
  const [inViewRef, inView] = useInView()

  const setTileRef = useCallback<RefCallback<HTMLElement>>((instance) => {
    inViewRef(instance)
  }, [inViewRef])

  useEffect(() => {
    if (inView && item && !hasTrackedImpression) {
      setHasTrackedImpression(true)
      trackClientEvent({
        subject: `merchandise-${tileType}-tile`,
        action: 'impression',
        category: 'shop',
        type: 'nonInteraction',
        context: [
          comluxgroup.createListItem_1_0_0({
            listName,
            listInstance: `${item.id}:${item.name}`,
            itemPosition: position + 1,
          }),
        ],
      })
    }
  }, [inView, item, hasTrackedImpression, tileType, listName, position])

  const onClick = useCallback(() => {
    if (!item) return
    trackClientEvent({
      subject: `merchandise-${tileType}-tile`,
      action: 'click',
      category: 'shop',
      type: 'interaction',
      context: [
        comluxgroup.createListItem_1_0_0({
          listName,
          listInstance: `${item.id}:${item.name}`,
          itemPosition: position + 1,
        }),
      ],
    })
  }, [tileType, item, listName, position])

  return { setTileRef, onClick }
}

export default useMerchandiseTileTracking
